import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ScanLine, Camera, CameraOff, CornerDownLeft } from "lucide-react";
import camApi from "@/lib/camApi";

// Browser-based scanning: prefers the native BarcodeDetector, falls back to jsQR
// (dynamic import), and always offers manual tag entry so it works on every device.
export default function Scanner() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [note, setNote] = useState("");
  const navigate = useNavigate();

  const resolve = async (tag) => {
    const clean = String(tag).trim();
    if (!clean) return;
    stop();
    try {
      const r = await camApi.assets({ q: clean });
      const exact = (r.assets || []).find((a) => a.asset_tag.toLowerCase() === clean.toLowerCase()) || (r.assets || [])[0];
      if (exact) { navigate(`/cam?asset=${exact.id}`); }
      else toast.error(`No asset found for "${clean}".`);
    } catch (e) { toast.error(e.message); }
  };

  const stop = () => {
    setScanning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };

  const start = async () => {
    setNote("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setScanning(true);

      const hasNative = "BarcodeDetector" in window;
      let detector = null, jsQR = null;
      if (hasNative) { try { detector = new window.BarcodeDetector({ formats: ["qr_code"] }); } catch { detector = null; } }
      if (!detector) { try { jsQR = (await import("jsqr")).default; } catch { jsQR = null; } }
      if (!detector && !jsQR) setNote("Live scanning isn't supported on this device — type the tag below.");

      const tick = async () => {
        const v = videoRef.current;
        if (!v || v.readyState < 2) { rafRef.current = requestAnimationFrame(tick); return; }
        try {
          if (detector) {
            const codes = await detector.detect(v);
            if (codes && codes[0]?.rawValue) return resolve(codes[0].rawValue);
          } else if (jsQR) {
            const c = canvasRef.current; const ctx = c.getContext("2d");
            c.width = v.videoWidth; c.height = v.videoHeight;
            ctx.drawImage(v, 0, 0, c.width, c.height);
            const img = ctx.getImageData(0, 0, c.width, c.height);
            const found = jsQR(img.data, img.width, img.height);
            if (found?.data) return resolve(found.data);
          }
        } catch { /* keep trying */ }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      setNote("Couldn't access the camera. Check permissions, or type the tag below.");
    }
  };

  useEffect(() => () => stop(), []);

  return (
    <div className="max-w-md mx-auto space-y-4">
      <div className="glass rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-1"><ScanLine className="h-4 w-4 text-cobalt-500" /><div className="overline">Scan an asset</div></div>
        <p className="text-sm text-muted-foreground mb-4">Point your camera at an asset's QR label to open its record and quick actions.</p>

        <div className="relative aspect-square rounded-xl overflow-hidden bg-black/90 grid place-items-center">
          <video ref={videoRef} className={`w-full h-full object-cover ${scanning ? "" : "hidden"}`} muted playsInline />
          <canvas ref={canvasRef} className="hidden" />
          {!scanning && <Camera className="h-10 w-10 text-white/40" />}
          {scanning && <div className="absolute inset-8 border-2 border-white/70 rounded-xl pointer-events-none" />}
        </div>

        {note && <p className="text-xs text-amber-600 mt-3">{note}</p>}

        <div className="mt-4">
          {scanning
            ? <Button variant="outline" className="w-full" onClick={stop}><CameraOff className="h-4 w-4 mr-1.5" />Stop scanning</Button>
            : <Button className="w-full bg-cobalt-500 hover:bg-cobalt-600" onClick={start}><Camera className="h-4 w-4 mr-1.5" />Start camera</Button>}
        </div>
      </div>

      <div className="glass rounded-2xl p-5">
        <div className="overline mb-2">Or enter a tag</div>
        <div className="flex gap-2">
          <Input className="text-mono text-sm" placeholder="e.g. BMSIT-DSK-00042" value={manual}
            onChange={(e) => setManual(e.target.value)} onKeyDown={(e) => e.key === "Enter" && resolve(manual)} />
          <Button onClick={() => resolve(manual)}><CornerDownLeft className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
